/**
 * Casca da aplicação: barra, menu, área, rodapé (design §3).
 *
 * O rodapé tem função dupla — lista os atalhos e é onde mora a última mensagem
 * do sistema, incluindo o desfazer (§4).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  api, lerSessao, limparSessao, NOME_DO_PAPEL, podeTudoQue,
  type Filial, type Papel, type Parceiro, type Produto, type Sessao,
} from "./api.ts";
import { Login } from "./telas/Login.tsx";
import { Orcamento } from "./telas/Orcamento.tsx";
import { Orcamentos, Pedidos, type ItemDocumento } from "./telas/Documentos.tsx";
import { Desdobro, Entrada, Pendencias } from "./telas/Movimentar.tsx";
import { Fiscal } from "./telas/Fiscal.tsx";
import { Financeiro } from "./telas/Financeiro.tsx";
import { Estoque } from "./telas/Estoque.tsx";
import { Gerencia } from "./telas/Gerencia.tsx";
import { Expedicao } from "./telas/Expedicao.tsx";
import { PainelGerencia } from "./telas/Painel.tsx";
import { Cadastros } from "./telas/Cadastros.tsx";
import { TrocarSenha, Usuarios } from "./telas/Usuarios.tsx";

type Tela =
  | "orcamentos" | "grade" | "pedidos" | "fiscal" | "financeiro"
  | "gerencia" | "estoque" | "movimentar" | "expedicao" | "cadastros"
  | "usuarios";

/**
 * Menu por papel.
 *
 * O pátio não vê Financeiro nem Gerência — e não é só para arrumar a tela: o
 * item existir e responder 403 ao ser clicado é pior que não existir, porque
 * o operador relata como defeito.
 *
 * Cadastros o pátio ALCANÇA, mas só para ler — e isso é correção, não
 * descuido. A API sempre liberou a leitura de produto, parceiro e espécie
 * (`exigirContexto`); era o menu que escondia. O operador que recebe um
 * fornecedor no portão precisa conferir qual regra de cubagem vale naquele
 * contrato, e antes não tinha onde olhar: a consulta tinha sido perdida junto
 * com a edição. Os botões de gravar ficam desabilitados com o motivo à vista,
 * que é diferente de existir e devolver 403.
 *
 * Isto decide o que MOSTRAR. Quem decide o que acontece é a API.
 */
const TELAS: { id: Tela; nome: string; minimo: Papel }[] = [
  { id: "orcamentos", nome: "Orçamentos", minimo: "ESCRITORIO" },
  { id: "pedidos", nome: "Pedidos", minimo: "ESCRITORIO" },
  { id: "fiscal", nome: "Notas fiscais", minimo: "ESCRITORIO" },
  { id: "financeiro", nome: "Financeiro", minimo: "ESCRITORIO" },
  { id: "gerencia", nome: "Gerência", minimo: "ESCRITORIO" },
  { id: "estoque", nome: "Estoque", minimo: "PATIO" },
  { id: "movimentar", nome: "Movimentar", minimo: "PATIO" },
  { id: "expedicao", nome: "Expedição", minimo: "PATIO" },
  { id: "cadastros", nome: "Cadastros", minimo: "PATIO" },
  { id: "usuarios", nome: "Usuários", minimo: "DONO" },
];

interface Mensagem { texto: string; desfazer?: (() => void) | undefined }

/**
 * Atalhos que a tela REALMENTE implementa.
 *
 * O rodapé anunciava os cinco atalhos da grade em todas as dez telas. Para o
 * pátio em Estoque, "Ctrl+Enter converter" é ação que ele nem tem permissão de
 * executar — e atalho que não faz nada quando apertado é indistinguível de
 * defeito. Só a grade de orçamento tem manipulador de tecla (Orcamento.tsx).
 */
const ATALHOS: Partial<Record<Tela, [string, string][]>> = {
  grade: [
    ["F2", "buscar produto"],
    ["F4", "nova linha"],
    ["F9", "salvar"],
    ["Ctrl+Enter", "converter"],
    ["Ctrl+Del", "remover linha"],
  ],
};

/**
 * Onde cada papel abre o sistema.
 *
 * O dono abre para ver como o negócio está, não para digitar orçamento — é o
 * que a própria Gerencia.tsx diz de si mesma ("os números que fazem o dono
 * abrir o sistema por vontade própria"). Os demais abrem na primeira tela que
 * alcançam.
 */
function telaInicial(papel: Papel, visiveis: typeof TELAS): Tela {
  if (papel === "DONO") return "gerencia";
  return visiveis[0]?.id ?? "estoque";
}

export function App() {
  const [sessao, setSessao] = useState<Sessao | null>(() => lerSessao());
  const papel: Papel = sessao?.usuario.papel ?? "PATIO";

  /**
   * Só as telas que este papel alcança. Para o pátio a lista tem duas linhas,
   * e a primeira delas é a tela inicial — abrir em Orçamentos e levar 403 seria
   * a primeira impressão do sistema.
   */
  const telasVisiveis = TELAS.filter((t) => podeTudoQue(papel, t.minimo));

  const [tela, setTela] = useState<Tela>(() => telaInicial(papel, telasVisiveis));
  const [trocandoSenha, setTrocandoSenha] = useState(false);
  /**
   * Painel do dono: fora do menu de propósito.
   *
   * Menu é para trabalho que se repete; o painel é consulta — entra, olha,
   * sai. Deixá-lo na lista o colocaria em pé de igualdade com Orçamentos e
   * Estoque, que é o oposto do que ele é.
   */
  const [painelAberto, setPainelAberto] = useState(false);
  const [semente, setSemente] = useState<{ itens: ItemDocumento[]; parceiroId: string } | null>(null);
  const [mensagem, setMensagem] = useState<Mensagem>({ texto: "" });
  const [online, setOnline] = useState(() => navigator.onLine);
  const [densidade, setDensidade] = useState<"compacta" | "confortavel">("compacta");

  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [parceiros, setParceiros] = useState<Parceiro[]>([]);
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [carregando, setCarregando] = useState(true);

  const timer = useRef<number | null>(null);

  /** Escreve no rodapé. O desfazer vive 8s, como manda o §5.2. */
  const aviso = useCallback((texto: string, desfazer?: () => void) => {
    if (timer.current !== null) window.clearTimeout(timer.current);
    setMensagem({ texto, desfazer });
    timer.current = window.setTimeout(() => setMensagem({ texto: "" }), 8000);
  }, []);

  useEffect(() => {
    const aoExpirar = () => { setSessao(null); };
    window.addEventListener("madeira:sessao-expirada", aoExpirar);
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("madeira:sessao-expirada", aoExpirar);
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  /**
   * Corrige a tela inicial quando a sessão chega.
   *
   * No primeiro render `sessao` ainda é null e o papel cai no padrão PATIO —
   * então quem entrava como dono ou escritório aterrissava em Estoque, a
   * primeira tela do papel de MENOR autoridade. Só não aparecia quando a
   * sessão já estava no localStorage.
   */
  useEffect(() => {
    if (!sessao) return;
    const p = sessao.usuario.papel;
    setTela(telaInicial(p, TELAS.filter((t) => podeTudoQue(p, t.minimo))));
  }, [sessao]);

  useEffect(() => {
    document.documentElement.setAttribute("data-densidade", densidade);
  }, [densidade]);

  /**
   * Recarrega os dados de referência.
   *
   * Cadastrar um produto novo precisa refletir na grade de orçamento sem
   * recarregar a página — senão o operador cadastra e não encontra.
   */
  const recarregarCadastros = useCallback(() => {
    Promise.all([
      api.get<Filial[]>("/filiais"),
      api.get<Parceiro[]>("/parceiros"),
      api.get<Produto[]>("/produtos"),
    ])
      .then(([f, pa, pr]) => { setFiliais(f); setParceiros(pa); setProdutos(pr); })
      .catch(() => {});
  }, []);

  /* Dados de referência: descem uma vez ao entrar. */
  useEffect(() => {
    if (!sessao) return;
    setCarregando(true);
    Promise.all([
      api.get<Filial[]>("/filiais"),
      api.get<Parceiro[]>("/parceiros"),
      api.get<Produto[]>("/produtos"),
    ])
      .then(([f, pa, pr]) => { setFiliais(f); setParceiros(pa); setProdutos(pr); })
      .catch((e) => aviso(e instanceof Error ? e.message : "falha ao carregar cadastros"))
      .finally(() => setCarregando(false));
  }, [sessao, aviso]);

  /**
   * Alt+1…4 troca de tela.
   *
   * Funciona sem disputa no PWA instalado (design §5.3); em aba de navegador
   * pode colidir com a troca de abas — incômodo de desenvolvimento, não de
   * produção.
   */
  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      const i = Number(e.key) - 1;
      // Indexa o que está na tela: com o menu filtrado, Alt+2 tem que levar ao
      // segundo item que a pessoa vê, não ao segundo da lista completa.
      const alvo = telasVisiveis[i];
      if (alvo) { e.preventDefault(); setTela(alvo.id); }
    }
    window.addEventListener("keydown", aoTeclar);
    return () => window.removeEventListener("keydown", aoTeclar);
  }, [telasVisiveis]);

  async function sair() {
    try { await api.sair(); } catch { /* sessão já podia estar morta */ }
    limparSessao();
    setSessao(null);
  }

  if (!sessao) return <Login aoEntrar={setSessao} />;

  return (
    <div className="app">
      <div className="barra">
        {/* Chapa clara deliberada: o PNG traz fundo claro embutido, e sobre a
            casca escura isso só funciona se parecer intencional — que é como
            marca se aplica sobre fundo escuro de qualquer forma. */}
        <span className="marca-chapa">
          <img src="/madesoft-icone.png" alt="MadeSoft" />
        </span>
        <span className="marca">MadeSoft</span>
        <span className="sep" />
        <span className="campo">{filiais.find((f) => f.matriz)?.nome ?? "—"}</span>
        <span className="dir">
          <span className={`pil-conexao ${online ? "pil-online" : "pil-offline"}`}>
            <i />{online ? "online" : "offline"}
          </span>
          <button
            className="menu-densidade foco-escuro"
            onClick={() => setDensidade((d) => (d === "compacta" ? "confortavel" : "compacta"))}
            style={{
              background: "none", border: "1px solid var(--divisor-escuro)",
              borderRadius: 3, color: "var(--menu-inativo)", fontSize: 11,
              padding: "3px 8px", cursor: "pointer",
            }}
            title="Altura da linha da grade"
          >
            {densidade === "compacta" ? "32px" : "40px"}
          </button>
          {/* Só o dono enxerga a porta. Quem não pode nem sabe que existe —
              e a API recusaria de qualquer forma: as rotas de gerência e de
              financeiro que alimentam o painel exigem papel. */}
          {podeTudoQue(papel, "DONO") && (
            <button
              className="foco-escuro"
              onClick={() => setPainelAberto(true)}
              title="Painel da Gerência"
              style={{
                background: "none", border: "1px solid var(--marca)",
                borderRadius: 3, color: "var(--marca-claro)", fontSize: 11.5,
                fontWeight: 600, padding: "3px 9px", cursor: "pointer",
                letterSpacing: 0.3,
              }}
            >
              Painel
            </button>
          )}
          <span className="campo">
            {sessao.usuario.nome}
            <span style={{ color: "var(--menu-atalho)", marginLeft: 6 }}>
              {NOME_DO_PAPEL[papel].toLowerCase()}
            </span>
          </span>
          <button
            className="foco-escuro"
            onClick={() => setTrocandoSenha(true)}
            style={{
              background: "none", border: 0, color: "var(--menu-atalho)",
              fontSize: 12, cursor: "pointer", padding: 4,
            }}
          >
            senha
          </button>
          <button
            className="foco-escuro"
            onClick={() => void sair()}
            style={{
              background: "none", border: 0, color: "var(--menu-atalho)",
              fontSize: 12, cursor: "pointer", padding: 4,
            }}
          >
            sair
          </button>
        </span>
      </div>

      <div className="corpo">
        <nav className="menu">
          {telasVisiveis.map((t, i) => (
            <button
              key={t.id}
              className={`foco-escuro${tela === t.id ? " on" : ""}`}
              onClick={() => setTela(t.id)}
            >
              {t.nome}<span className="k">{i < 9 ? `Alt+${i + 1}` : ""}</span>
            </button>
          ))}
        </nav>

        {/* Gerência e Financeiro são tela de mesa: ninguém as abre no pátio
            (o menu nem as mostra para o papel PATIO). A densidade e o rótulo
            mono caixa-alta existem para quem digita em pé — aqui atrapalham
            mais do que ajudam. Superfície própria, mesmos tokens. */}
        <main
          className={
            tela === "gerencia" || tela === "financeiro"
              ? "area area-escritorio"
              : "area"
          }
        >
          {carregando ? (
            <div className="esq" style={{ padding: 14 }}>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div className="l" key={i}>
                  <span className="b" /><span className="b" /><span className="b" /><span className="b" />
                </div>
              ))}
            </div>
          ) : tela === "orcamentos" ? (
            <Orcamentos
              aviso={aviso}
              aoNovo={() => { setSemente(null); setTela("grade"); }}
              aoDuplicar={(itens, parceiroId) => {
                setSemente({ itens, parceiroId });
                setTela("grade");
                aviso(`${itens.length} linha(s) copiadas para uma grade nova`);
              }}
            />
          ) : tela === "grade" ? (
            <Orcamento
              filiais={filiais} parceiros={parceiros} produtos={produtos}
              aviso={aviso} semente={semente}
              aoSalvar={() => { setSemente(null); setTela("orcamentos"); }}
            />
          ) : tela === "pedidos" ? (
            <Pedidos aviso={aviso} />
          ) : tela === "fiscal" ? (
            <Fiscal aviso={aviso} papel={papel} />
          ) : tela === "financeiro" ? (
            <Financeiro aviso={aviso} />
          ) : tela === "gerencia" ? (
            <Gerencia />
          ) : tela === "estoque" ? (
            <Estoque produtos={produtos} />
          ) : tela === "movimentar" ? (
            <div className="rolagem" style={{ display: "grid", gap: 14, padding: 14, alignContent: "start" }}>
              <Pendencias />
              <Entrada filiais={filiais} parceiros={parceiros} produtos={produtos} aviso={aviso} />
              <Desdobro filiais={filiais} produtos={produtos} aviso={aviso} />
            </div>
          ) : tela === "expedicao" ? (
            <Expedicao aviso={aviso} papel={papel} filiais={filiais} />
          ) : tela === "usuarios" ? (
            <Usuarios aviso={aviso} />
          ) : (
            <Cadastros
              aviso={aviso}
              aoMudarCadastro={recarregarCadastros}
              papel={papel}
            />
          )}
        </main>
      </div>

      {trocandoSenha && (
        <TrocarSenha aviso={aviso} aoFechar={() => setTrocandoSenha(false)} />
      )}

      {/* Dupla checagem do papel: o botão pode sumir, o estado pode ficar. */}
      {painelAberto && podeTudoQue(papel, "DONO") && (
        <PainelGerencia aoFechar={() => setPainelAberto(false)} />
      )}

      <div className="rodape">
        {/* Alt+N é o único atalho global — vale em toda tela. */}
        <span className="at">
          <kbd>Alt+1…{telasVisiveis.length}</kbd> trocar de tela
        </span>
        {(ATALHOS[tela] ?? []).map(([tecla, acao]) => (
          <span className="at" key={tecla}>
            <kbd>{tecla}</kbd> {acao}
          </span>
        ))}
        <span className="msg">
          {mensagem.texto}
          {mensagem.desfazer && (
            <button className="foco-escuro" onClick={mensagem.desfazer}>Ctrl+Z desfaz</button>
          )}
        </span>
      </div>
    </div>
  );
}
